<template>
  <FormularioCadastro
    module-name="Configuração de Job (Robô)"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="salvarJob"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
  >
    <!-- Botões no Cabeçalho (Histórico e Auditoria) -->
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="abrirHistorico"
        title="Ver Histórico de Execuções"
      >
        <i class="bi bi-journal-text"></i>
      </button>
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="mostrarAuditoria = true"
        title="Ver Histórico de Alterações (Auditoria)"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div class="card mb-3">
      <div class="card-header">
        <h5 class="mb-0"><i class="bi bi-gear-wide-connected me-2"></i>Definições do Motor</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoSelect
              id="nomeJob"
              label="Job Disponível"
              v-model="item.nomeJob"
              :options="jobsDisponiveis"
              option-value="codigo"
              option-label="descricao"
              required
              :disabled="isEditing"
              @update:model-value="onJobSelecionado"
            />
            <div v-if="!isEditing && jobSelecionado" class="form-text small">
              <i class="bi bi-info-circle me-1"></i>{{ jobSelecionado.descricaoCompleta }}
            </div>
          </div>
          <div class="col-md-3">
            <CampoSelect
              id="isAtivo"
              label="Job Ativo?"
              v-model="item.isAtivo"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              required
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="maxTentativas"
              label="Máx. Tentativas"
              type="number"
              min="1"
              v-model.number="item.maxTentativas"
              required
            />
          </div>
          <div class="col-md-12">
            <CampoInput
              id="descricao"
              label="Descrição Funcional"
              v-model="item.descricao"
              placeholder="O que este robô faz?"
              maxlength="200"
              :disabled="!!jobSelecionado"
            />
            <div v-if="jobSelecionado" class="form-text small text-muted">
              <i class="bi bi-lock-fill me-1"></i>Descrição preenchida automaticamente pelo sistema
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-header">
        <h5 class="mb-0"><i class="bi bi-calendar-week me-2"></i>Agendamento e Resiliência</h5>
      </div>
      <div class="card-body">
        <div class="alert alert-secondary border-0 shadow-sm small py-2 mb-4 d-flex align-items-start">
          <i class="bi bi-info-circle-fill me-2 mt-1"></i>
          <div>
            A frequência de execução utiliza a sintaxe <strong>Cron</strong> padrão (Minuto, Hora, Dia do Mês, Mês, Dia da Semana).
            <br>Exemplos: <code>0 2 * * *</code> (Todos os dias às 02:00) | <code>0 * * * *</code> (De hora a hora) | <code>0 8 * * 1</code> (Toda segunda-feira às 08:00).
          </div>
        </div>

        <div class="row g-3 align-items-center mb-4">
          <div class="col-md-4">
            <CampoInput
              id="expressaoCron"
              label="Expressão Cron"
              v-model="item.expressaoCron"
              placeholder="Ex: 0 2 * * *"
              required
              maxlength="50"
            />
          </div>
        </div>

        <div class="alert alert-warning border-0 shadow-sm small py-2 mb-3">
          <i class="bi bi-shield-exclamation me-2"></i><strong>Prevenção de Falhas:</strong> Se o Job falhar <strong>{{ item.maxTentativas || 3 }}</strong> vezes consecutivas, será automaticamente inativado por segurança.
        </div>

        <div class="row g-3">
          <div class="col-md-6">
            <CampoInput
              id="emailAlerta"
              label="E-mail para Alerta de Falha Crítica"
              type="email"
              v-model="item.emailAlertaFalha"
              placeholder="ti@suaempresa.com"
              maxlength="255"
            />
          </div>
        </div>
      </div>
    </div>
  </FormularioCadastro>

  <!-- MODAL DE HISTÓRICO DE EXECUÇÕES (No Cadastro) -->
  <ModalFormulario
    v-model="modalHistoricoAberto"
    :titulo="`Histórico: ${item?.nomeJob}`"
    tamanho="xl"
    :ocultar-salvar="true"
    texto-cancelar="Fechar"
  >
    <div v-if="loadingHistorico" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-primary" role="status"></div> A carregar histórico...
    </div>
    <div v-else class="admin-table-container border-0 shadow-none m-0">
      <table class="table table-sm admin-table m-0">
        <thead>
          <tr>
            <th>Início (UTC)</th>
            <th>Fim (UTC)</th>
            <th class="text-center">Origem</th>
            <th class="text-center">Tentativa</th>
            <th>Duração</th>
            <th>Registos</th>
            <th>Status</th>
            <th class="text-center">Detalhes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="historicoLogs.length === 0">
            <td colspan="8" class="text-center text-muted py-3">Nenhuma execução registada para este Job.</td>
          </tr>
          <tr v-for="log in historicoLogs" :key="log.id">
            <td>{{ formatarDataHoraBR(log.dataHoraInicio) }}</td>
            <td>{{ formatarDataHoraBR(log.dataHoraFim) }}</td>
            <td class="text-center">
              <span class="badge bg-body-secondary text-body border">{{ log.tipoExecucao || 'Agendado' }}</span>
            </td>
            <td class="text-center font-monospace">{{ log.tentativa || 1 }}</td>
            <td>{{ log.duracaoSegundos ? log.duracaoSegundos.toFixed(1) + 's' : '-' }}</td>
            <td>{{ log.registrosProcessados }}</td>
            <td><span :class="obterClasseStatusLog(log.status)">{{ log.status }}</span></td>
            <td class="text-center">
               <button v-if="log.mensagemErro || log.detalhesJson" class="btn btn-sm btn-link py-0" @click="verDetalheErro(log)">Ver</button>
               <span v-else class="text-muted">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </ModalFormulario>

  <!-- MODAL PARA VER ERRO COPIÁVEL -->
  <ModalFormulario
    v-model="modalErroAberto"
    titulo="Detalhes do Log"
    tamanho="lg"
    :ocultar-salvar="true"
    texto-cancelar="Fechar"
  >
    <div class="alert alert-warning mb-2 small py-2"><i class="bi bi-info-circle me-1"></i> Pode selecionar e copiar o texto abaixo.</div>
    <pre class="bg-dark text-light p-3 rounded" style="white-space: pre-wrap; word-break: break-all; max-height: 60vh; overflow-y: auto;">{{ logErroSelecionado }}</pre>
  </ModalFormulario>

  <!-- MODAL DE AUDITORIA -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="JobConfig"
    :chave-primaria="item.id"
    :titulo-registro="item.nomeJob"
    @fechar="mostrarAuditoria = false"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { formatarDataHoraBR } from '@/utils/useDataBR'

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      id: 0,
      nomeJob: '',
      descricao: '',
      expressaoCron: '0 2 * * *',
      isAtivo: true,
      maxTentativas: 3,
      emailAlertaFalha: ''
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification', 'changesDetected'])

// Lista hardcoded de jobs disponíveis no sistema
const jobsDisponiveis = [
  {
    codigo: 'SYNC_NCM_CADA',
    descricao: 'Sincronização NCM - Siscomex',
    descricaoCompleta: 'Sincroniza códigos NCM e atributos do Siscomex/CADA'
  },
  {
    codigo: 'SYNC_PTAX_BACEN',
    descricao: 'Sincronização PTAX - Banco Central',
    descricaoCompleta: 'Busca cotações PTAX de venda do Banco Central (API Olinda)'
  }
]

const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

const mapaCampos = {
  nomeJob: 'Nome do Job',
  expressaoCron: 'Expressão Cron',
  maxTentativas: 'Máx. Tentativas'
}

const { item, isLoading, isEditing, entityInfo, submitForm, handleCancel } = useFormulario(props, emit, { mapaCampos })

const jobSelecionado = computed(() => {
  return jobsDisponiveis.find(j => j.codigo === item.value.nomeJob)
})

const onJobSelecionado = (codigoJob) => {
  const job = jobsDisponiveis.find(j => j.codigo === codigoJob)
  if (job) {
    item.value.descricao = job.descricaoCompleta
  }
}

// --- Lógica do Histórico e Modal de Erro ---
const modalHistoricoAberto = ref(false)
const historicoLogs = ref([])
const loadingHistorico = ref(false)

const modalErroAberto = ref(false)
const logErroSelecionado = ref('')

const mostrarAuditoria = ref(false)

const abrirHistorico = async () => {
  if (!item.value.nomeJob) return
  historicoLogs.value = []
  loadingHistorico.value = true
  modalHistoricoAberto.value = true

  try {
    const res = await api.get(`/JobLogs?nomeJob=${item.value.nomeJob}&pageNumber=1&pageSize=15`)
    historicoLogs.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar histórico.', type: 'error' })
  } finally {
    loadingHistorico.value = false
  }
}

const verDetalheErro = (log) => {
  logErroSelecionado.value = log.mensagemErro ? `[ERRO TÉCNICO]\n\n${log.mensagemErro}` : `[MÉTRICAS]\n\n${log.detalhesJson}`
  modalErroAberto.value = true
}

const obterClasseStatusLog = (status) => {
  if (status === 'Sucesso') return 'badge bg-success'
  if (status === 'Falha') return 'badge bg-danger'
  if (status === 'Em Andamento') return 'badge bg-warning text-dark'
  return 'badge bg-secondary'
}

const salvarJob = async () => {
  const payload = {
    ...item.value,
    isAtivo: item.value.isAtivo === true || String(item.value.isAtivo) === 'true',
    maxTentativas: parseInt(item.value.maxTentativas, 10) || 3
  }
  await submitForm('JobConfigs', 'id', payload)
}
</script>

<style scoped>

.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.375rem;
  overflow-x: auto;
}
.admin-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-bottom: 2px solid var(--bs-border-color-translucent);
}
.admin-table td {
  border-bottom: 1px solid var(--bs-border-color-translucent);
  color: var(--bs-body-color);
  vertical-align: middle;
}
</style>
